import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, 
  X, 
  AlertTriangle,
  FileText,
  Settings,
  Shield,
  Calendar,
  Target,
  TrendingUp,
  User,
  ChevronDown,
  Search,
  Database
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';

const CreateRisk = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // Tab management
  const [activeTab, setActiveTab] = useState('basic');

  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    category: '',
    status: 'Open',
    priority: 'Medium',
    impact: 'Medium',
    probability: 'Medium',
    riskScore: 0,
    
    // Project & Ownership
    project: '',
    owner: '',
    identifiedDate: new Date().toISOString().split('T')[0],
    targetDate: '',
    
    // Risk Assessment
    mitigationStrategy: '',
    contingencyPlan: '',
    riskFactors: [],
    
    // Additional Details
    notes: '',
    attachments: [],
    tags: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [testMode, setTestMode] = useState(false);
  
  // Searchable dropdown state for Project
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef(null);

  useEffect(() => {
    loadProjects();
    loadUsers();
    loadTestMode();
  }, []);

  const loadTestMode = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setTestMode(result.data.testMode ?? false);
        }
      }
    } catch (error) {
      console.error('Error loading test mode setting:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS}`, {
        headers: API_CONFIG.HEADERS
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectDropdownOpen]);

  const loadProjects = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        headers: API_CONFIG.HEADERS
    });
    if (!response.ok) {
      throw new Error('Failed to load projects');
    }

    const result = await response.json();

    const projectsList =
      result?.data?.projects ||
      result?.projects ||
      [];

    setProjects(projectsList);
  } catch (error) {
    console.error('Error loading projects:', error);
    setProjects([]);
  }
};


  // Filter projects based on search term
  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
    (project.projectCode && project.projectCode.toLowerCase().includes(projectSearchTerm.toLowerCase()))
  );

  // Get selected project name for display
  const selectedProject = projects.find(p => {
    const projectId = p.id || p._id;
    return projectId === formData.project;
  });

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info',
      icon: <FileText size={18} />
    },
    {
      id: 'assessment',
      label: 'Assessment',
      icon: <Target size={18} />
    },
    {
      id: 'mitigation',
      label: 'Mitigation',
      icon: <Shield size={18} />
    },
    {
      id: 'additional',
      label: 'Additional',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Calculate risk score if impact or probability changes
      if (field === 'impact' || field === 'probability') {
        updated.riskScore = calculateRiskScore(updated.impact, updated.probability);
      }
      
      return updated;
    });
  };

  const calculateRiskScore = (impact, probability) => {
    const impactScores = { 'Low': 2, 'Medium': 3, 'High': 5 };
    const probabilityScores = { 'Low': 2, 'Medium': 3, 'High': 5 };
    
    const impactScore = impactScores[impact] || 1;
    const probabilityScore = probabilityScores[probability] || 1;
    
    return impactScore * probabilityScore;
  };

  const handleArrayAdd = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map form data to backend format
      // Note: riskType is required by backend, using category as fallback
      // Handle owner - can be user ID or name string
      let ownerId = null;
      let ownerName = formData.owner;
      
      // Check if owner is a valid ObjectId (user ID)
      if (formData.owner && /^[0-9a-fA-F]{24}$/.test(formData.owner)) {
        ownerId = formData.owner;
        // Try to get owner name from users list
        const ownerUser = users.find(u => (u.id || u._id) === formData.owner);
        if (ownerUser) {
          ownerName = ownerUser.firstName && ownerUser.lastName 
            ? `${ownerUser.firstName} ${ownerUser.lastName}` 
            : ownerUser.name || formData.owner;
        }
      }
      
      const riskData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        status: formData.status,
        priority: formData.priority,
        impact: formData.impact,
        probability: formData.probability,
        riskScore: formData.riskScore,
        project: formData.project,
        owner: ownerId, // ObjectId if valid, otherwise null
        ownerName: ownerName, // Always store the name
        riskType: formData.category || 'General', // Required field, using category as fallback
        identifiedDate: formData.identifiedDate || new Date().toISOString(),
        targetDate: formData.targetDate || null,
        mitigationStrategy: formData.mitigationStrategy || '',
        contingencyPlan: formData.contingencyPlan || '',
        notes: formData.notes || '',
        tags: formData.tags || []
      };

      const response = await fetch(`${API_CONFIG.ENDPOINTS.RISKS}`, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(riskData)
      });

      if (response.ok) {
        success('Risk created successfully!');
        // Navigate after a short delay to allow toast to show
        setTimeout(() => {
          navigate('/projects/risks');
        }, 1000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.message || 'Failed to create risk. Please check all required fields.');
      }
    } catch (err) {
      console.error('Error creating risk:', err);
      showError(`Network error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects/risks');
  };

  const fillTestData = () => {
    // Get first available project and user
    const firstProject = projects.length > 0 ? (projects[0].id || projects[0]._id) : '';
    const firstUser = users.length > 0 ? (users[0].id || users[0]._id) : '';
    
    setFormData({
      name: 'Budget Overrun Risk',
      description: 'Risk of exceeding project budget due to scope creep and unexpected costs. This risk may impact project timeline and resource allocation.',
      category: 'Financial',
      status: 'Open',
      priority: 'High',
      impact: 'High',
      probability: 'Medium',
      riskScore: calculateRiskScore('High', 'Medium'),
      project: firstProject,
      owner: firstUser,
      identifiedDate: new Date().toISOString().split('T')[0],
      targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 60 days from now
      mitigationStrategy: 'Regular budget reviews and scope control. Implement change management process. Set up budget alerts and variance thresholds.',
      contingencyPlan: 'If budget overrun occurs, prioritize critical features and defer non-essential items. Negotiate with stakeholders for additional funding or scope reduction.',
      riskFactors: [
        'Scope creep from stakeholders',
        'Inflation in material costs',
        'Unforeseen technical challenges',
        'Resource availability issues'
      ],
      notes: 'This is a test risk created for demonstration purposes. Monitor closely and update status regularly.',
      attachments: [],
      tags: ['budget', 'financial', 'high-priority']
    });
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Create New Risk"
        description="Identify and document a new project risk with assessment and mitigation strategies"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="team-form-section">
                <h4 className="mb-4">Basic Information</h4>
                
                <div className="form-grid-3">
                  <div className="form-group">
                    <label className="form-label">Project *</label>
                    <div ref={projectDropdownRef} style={{ position: 'relative' }}>
                      <div
                        className="form-control"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          minHeight: '38px'
                        }}
                        onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                      >
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {selectedProject ? (
                            <span style={{ color: '#1f2937' }}>
                              {selectedProject.name} {selectedProject.projectCode ? `(${selectedProject.projectCode})` : ''}
                            </span>
                          ) : formData.project ? (
                            <span style={{ color: '#9ca3af' }}>Project ID: {formData.project} (not found)</span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>Select Project</span>
                          )}
                        </div>
                        <ChevronDown 
                          size={18} 
                          style={{ 
                            color: '#6b7280',
                            transform: isProjectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                          }} 
                        />
                      </div>
                      
                      {isProjectDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          zIndex: 1000,
                          marginTop: '4px',
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {/* Search Input */}
                          <div style={{ 
                            padding: '8px',
                            borderBottom: '1px solid #e5e7eb',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white'
                          }}>
                            <div style={{ position: 'relative' }}>
                              <Search 
                                size={16} 
                                style={{ 
                                  position: 'absolute',
                                  left: '12px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: '#9ca3af'
                                }} 
                              />
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search projects..."
                                value={projectSearchTerm}
                                onChange={(e) => {
                                  setProjectSearchTerm(e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '36px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px'
                                }}
                                autoFocus
                              />
                            </div>
                          </div>
                          
                          {/* Options List */}
                          <div style={{
                            overflowY: 'auto',
                            maxHeight: '250px'
                          }}>
                            {filteredProjects.length > 0 ? (
                              filteredProjects.map(project => {
                                const projectId = project.id || project._id;
                                return (
                                  <div
                                    key={projectId}
                                    onClick={() => {
                                      handleInputChange('project', projectId);
                                      setIsProjectDropdownOpen(false);
                                      setProjectSearchTerm('');
                                    }}
                                    style={{
                                      padding: '10px 16px',
                                      cursor: 'pointer',
                                      backgroundColor: formData.project === projectId ? '#eff6ff' : 'white',
                                      color: formData.project === projectId ? '#1e40af' : '#1f2937',
                                      borderBottom: '1px solid #f3f4f6',
                                      transition: 'background-color 0.15s'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (formData.project !== projectId) {
                                        e.target.style.backgroundColor = '#f9fafb';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (formData.project !== projectId) {
                                        e.target.style.backgroundColor = 'white';
                                      }
                                    }}
                                  >
                                    <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                      {project.name}
                                    </div>
                                    {project.projectCode && (
                                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                        {project.projectCode}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{
                                padding: '16px',
                                textAlign: 'center',
                                color: '#6b7280',
                                fontSize: '14px'
                              }}>
                                No projects found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Risk Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter risk name"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select
                      className="form-select"
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="Financial">Financial</option>
                      <option value="Resource">Resource</option>
                      <option value="Technical">Technical</option>
                      <option value="Compliance">Compliance</option>
                      <option value="Vendor">Vendor</option>
                      <option value="Schedule">Schedule</option>
                      <option value="Quality">Quality</option>
                      <option value="Security">Security</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe the risk in detail..."
                    required
                  />
                </div>
                
                <div className="form-grid-3">
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="Open">Open</option>
                      <option value="Mitigated">Mitigated</option>
                      <option value="Closed">Closed</option>
                      <option value="Monitoring">Monitoring</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select
                      className="form-select"
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Risk Owner *</label>
                    <select
                      className="form-select"
                      value={formData.owner}
                      onChange={(e) => handleInputChange('owner', e.target.value)}
                      required
                    >
                      <option value="">Select Owner</option>
                      <option value="John Smith">John Smith</option>
                      <option value="Sarah Johnson">Sarah Johnson</option>
                      <option value="Mike Chen">Mike Chen</option>
                      <option value="Lisa Garcia">Lisa Garcia</option>
                      <option value="David Wilson">David Wilson</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-grid-3">
                  <div className="form-group">
                    <label className="form-label">Identified Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.identifiedDate}
                      onChange={(e) => handleInputChange('identifiedDate', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Target Resolution Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.targetDate}
                      onChange={(e) => handleInputChange('targetDate', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    {/* Empty space to maintain 3-column layout */}
                  </div>
                </div>
              </div>
            )}

            {/* Risk Assessment Tab */}
            {activeTab === 'assessment' && (
              <div className="team-form-section">
                <h4 className="mb-4">Risk Assessment</h4>
                
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Impact *</label>
                    <select
                      className="form-select"
                      value={formData.impact}
                      onChange={(e) => handleInputChange('impact', e.target.value)}
                      required
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Probability *</label>
                    <select
                      className="form-select"
                      value={formData.probability}
                      onChange={(e) => handleInputChange('probability', e.target.value)}
                      required
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Risk Score</label>
                  <div className="alert alert-info" style={{ 
                    backgroundColor: '#eff6ff', 
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    padding: '12px',
                    marginTop: '8px'
                  }}>
                    <strong>Calculated Score: {formData.riskScore}</strong>
                    <p className="mb-0" style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                      Impact ({formData.impact}) × Probability ({formData.probability})
                    </p>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Risk Factors</label>
                  <div className="array-input-group">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add a risk factor"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleArrayAdd('riskFactors', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={(e) => {
                          const input = e.target.previousElementSibling;
                          if (input && input.value.trim()) {
                            handleArrayAdd('riskFactors', input.value);
                            input.value = '';
                          }
                        }}
                      >
                        <X size={16} style={{ transform: 'rotate(45deg)' }} />
                      </button>
                    </div>
                    <div className="array-items">
                      {formData.riskFactors.map((factor, index) => (
                        <div key={index} className="array-item">
                          <span>{factor}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleArrayRemove('riskFactors', index)}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mitigation Tab */}
            {activeTab === 'mitigation' && (
              <div className="team-form-section">
                <h4 className="mb-4">Mitigation Strategy</h4>
                
                <div className="form-group">
                  <label className="form-label">Mitigation Strategy *</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={formData.mitigationStrategy}
                    onChange={(e) => handleInputChange('mitigationStrategy', e.target.value)}
                    placeholder="Describe the strategy to mitigate this risk..."
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Contingency Plan</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.contingencyPlan}
                    onChange={(e) => handleInputChange('contingencyPlan', e.target.value)}
                    placeholder="Describe the contingency plan if the risk materializes..."
                  />
                </div>
              </div>
            )}

            {/* Additional Information Tab */}
            {activeTab === 'additional' && (
              <div className="team-form-section">
                <h4 className="mb-4">Additional Information</h4>
                
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Add any additional notes or comments..."
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Tags</label>
                  <div className="array-input-group">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add a tag"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleArrayAdd('tags', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={(e) => {
                          const input = e.target.previousElementSibling;
                          if (input && input.value.trim()) {
                            handleArrayAdd('tags', input.value);
                            input.value = '';
                          }
                        }}
                      >
                        <X size={16} style={{ transform: 'rotate(45deg)' }} />
                      </button>
                    </div>
                    <div className="array-items">
                      {formData.tags.map((tag, index) => (
                        <div key={index} className="array-item">
                          <span>{tag}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleArrayRemove('tags', index)}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {testMode && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={fillTestData}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    backgroundColor: '#ffffff',
                    borderColor: '#d1d5db'
                  }}
                >
                  <Database size={18} />
                  Fill Test Data
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Creating Risk...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Create Risk
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateRisk;

